import type { ReactElement } from 'react'
import { Fragment } from 'react'
import { Navigate, Outlet, Route, Routes } from 'react-router-dom'
import { AppShell } from '../../shared/layout/AppShell'
import { AvisoProgramacao } from '../../shared/layout/AvisoProgramacao'
import { LoginPage } from '../../features/auth/LoginPage'
import { EsqueciSenhaPage } from '../../features/auth/EsqueciSenhaPage'
import { PrimeiroAcessoPage } from '../../features/auth/PrimeiroAcessoPage'
import { SemAcessoPage } from '../../features/auth/SemAcessoPage'
import { PlaceholderPage } from '../../shared/components/PlaceholderPage'
import { PORTAL_RESOURCE_CONFIGS, portalUsuariosConfig } from '../../features/cadastros/configs/portal'
import { rotasDaLogistica } from '../../areas/logistica/routes'
import { rotasDoAdministrativo } from '../../areas/administrativo/routes'
import { rotasDoDp } from '../../areas/dp/routes'
import { rotasDoAbastecimento } from '../../areas/abastecimento/routes'
import { AREAS_PATH } from '../areas/areas'
import { LegacyBackofficeRedirect } from '../areas/LegacyBackofficeRedirect'
import { CustomerAdminOnly, HomeRedirect, PermissionGate, RequireAuth, RoleGate, StaffOnly } from './guards'
import { flattenNav, portalNav } from './navConfig'
import { SEM_ACESSO_PATH } from './paths'
import {
  AreasPage, ConfiguracoesPage, ContractDetailPage, ContractsPage, MinhasCargasPage, PortalHome, ProgramacaoPage,
  PortalSolicitacoesPage, ResourceFormPage, ResourceListPage, SegurancaPage, SolicitarCargasPage,
  UsuarioFormPage, comPermissao,
} from './telas'
import { useTituloDaAba } from './tituloDaAba'

/**
 * Portal do cliente: mapa path → componente, escopado. Reaproveita
 * ContractsPage/ContractDetailPage (cientes de papel) e os cadastros do
 * cliente (PORTAL_RESOURCE_CONFIGS). "Usuários" fica atrás do CustomerAdminOnly.
 */
const PORTAL_PAGES: Record<string, ReactElement> = {
  // Operação
  '/portal': <PortalHome />,
  '/portal/programacao': <ProgramacaoPage />,
  '/portal/tickets': <PortalSolicitacoesPage />,
  '/portal/cargas': <MinhasCargasPage />,
  // Gestão
  '/portal/contracts': <ContractsPage />,
  // Cadastros do portal (lista config-driven; inclui /portal/usuarios)
  ...Object.fromEntries(PORTAL_RESOURCE_CONFIGS.map((cfg) => [cfg.basePath, <ResourceListPage key={cfg.basePath} config={cfg} />])),
}

/** Hub → Áreas → Módulos → Telas (24/10/2026). Cada área declara as próprias rotas
 *  em `src/areas/<área>/routes.tsx`; aqui elas só se juntam. Ver docs/areas. */
export function AppRoutes() {
  useTituloDaAba()
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/esqueci-senha" element={<EsqueciSenhaPage />} />
      {/* Fora do RequireAuth de propósito: a sessão existe, mas é parcial, e o
          guard manda para cá justamente quem ele não deixa passar. */}
      <Route path="/primeiro-acesso" element={<PrimeiroAcessoPage />} />

      {/* Os endereços de antes das áreas levam aos novos, com busca e âncora. Fora
          do RequireAuth: o destino passa por ele de qualquer jeito. */}
      <Route path="/backoffice/*" element={<LegacyBackofficeRedirect />} />

      <Route element={<RequireAuth />}>
        {/* A tela de escolha de área tem barra própria, sem a barra lateral: ainda
            não há área escolhida para a barra mostrar. */}
        <Route element={<StaffOnly />}>
          {/* O aviso de programação do cliente vale aqui também: é a primeira tela
              da equipe depois do login. Nas demais, vem da casca. */}
          <Route path={AREAS_PATH} element={<><AreasPage /><AvisoProgramacao /></>} />
        </Route>

        <Route element={<AppShell />}>
          <Route path="/" element={<HomeRedirect />} />

          {/* Piso da navegação: sessão válida que não alcança tela nenhuma. Fica
              fora de todo gate, de propósito — é para cá que homePathFor manda
              quem não tem nenhuma área, e gateá-la fecharia o laço com os portões. */}
          <Route path={SEM_ACESSO_PATH} element={<SemAcessoPage />} />

          {/* Segurança da própria conta. Fica FORA do gate de staff porque o
              segundo fator vale para os dois lados, e é a única tela que precisa
              continuar alcançável por quem tem MFA obrigatório ainda pendente. */}
          <Route path="/conta/seguranca" element={<SegurancaPage />} />
          <Route path="/conta/configuracoes" element={<ConfiguracoesPage />} />

          {/* As áreas da equipe. Cada uma tem o portão da área na raiz e o da
              permissão em cada tela. */}
          <Route element={<StaffOnly />}>
            {rotasDaLogistica()}
            {rotasDoAdministrativo()}
            {rotasDoDp()}
            {rotasDoAbastecimento()}
          </Route>

          {/* Portal do cliente: outra entrada, fora das áreas. */}
          <Route element={<RoleGate allow="cliente"><Outlet /></RoleGate>}>
            {/* Itens do nav (exceto Usuários, que tem guard próprio). */}
            {flattenNav(portalNav)
              .filter((item) => !item.adminOnly)
              .map((item) => (
                <Route
                  key={item.path}
                  path={item.path}
                  element={comPermissao(item, PORTAL_PAGES[item.path] ?? <PlaceholderPage title={item.label} />)}
                />
              ))}

            {/* Solicitar cargas — tela dedicada (fora do nav, aberta pelo botão). O
                portão vai à mão: a rota não passa por `comPermissao`, e sem ele a
                URL digitada montava o formulário para quem o servidor recusaria. */}
            <Route
              path="/portal/solicitar"
              element={<PermissionGate permission="unloading.tickets_manage"><SolicitarCargasPage /></PermissionGate>}
            />

            {/* Contratos: só o detalhe (view-only; ContractDetailPage esconde "Editar"). */}
            <Route path="/portal/contracts/:uuid" element={<ContractDetailPage />} />

            {/* Cadastros do portal: criar/editar (exceto Usuários). */}
            {PORTAL_RESOURCE_CONFIGS.filter((cfg) => cfg.basePath !== '/portal/usuarios').map((cfg) => (
              <Fragment key={`portal-form-${cfg.basePath}`}>
                <Route path={`${cfg.basePath}/new`} element={<ResourceFormPage config={cfg} />} />
                <Route path={`${cfg.basePath}/:uuid`} element={<ResourceFormPage config={cfg} />} />
              </Fragment>
            ))}

            {/* Usuários (sub-usuários do cliente) — só o admin do cliente. */}
            <Route element={<CustomerAdminOnly />}>
              <Route path="/portal/usuarios" element={<ResourceListPage config={portalUsuariosConfig} />} />
              <Route path="/portal/usuarios/new" element={<UsuarioFormPage variante="portal" />} />
              <Route path="/portal/usuarios/:uuid" element={<UsuarioFormPage variante="portal" />} />
            </Route>
          </Route>
        </Route>
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}
